import { type FC, useEffect, useMemo, useState } from 'react';
import { BsCaretDown } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { first } from 'lodash';

import { type RootState } from '@editor2/reducers/root';
import { CollapseState } from '@editor2/types/collapseState';

import { metadataBlock } from './styles';
import { Button, Col, Form } from 'react-bootstrap';

interface Props {
	courseId: number;
	metadata: { [key: string]: string };
	onDebouncedChange: (_) => void;
}

const Metadata: FC<Props> = (props) => {
	const { courseId, metadata, onDebouncedChange } = props;

	const [collapsed, setCollapsed] = useState(true);
	const { collapseState } = useSelector((state: RootState) => state.courses[courseId]);

	useEffect(() => {
		switch (collapseState.elements) {
			case CollapseState.ExpandAllQuestions:
			case CollapseState.ExpandAll:
				setCollapsed(false);
				break;

			case CollapseState.CollapseAll:
				setCollapsed(true);
				break;
		}
	}, [collapseState.elements]);

	const { metadata: configMetadata } = useSelector(
		(state: RootState) => state.courses[courseId].config
	);
	const presetFields = useMemo(
		() => configMetadata?.editor_ui?.preset_fields || {},
		[configMetadata]
	);

	const presetKeys = useMemo(() => Object.keys(presetFields), [presetFields]);
	const nonPresetKeys = useMemo(
		() => Object.keys(metadata).filter((key) => !presetKeys.includes(key)),
		[metadata, presetKeys]
	);

	const initialRows = useMemo(() => {
		const presetRows = presetKeys.map((key) => [key, metadata[key] || '']);
		const nonPresetRows = nonPresetKeys.map((key) => [key, metadata[key]]);

		return [...presetRows, ...nonPresetRows];
	}, [presetKeys, nonPresetKeys, metadata]);

	const [rows, setRows] = useState(initialRows.length ? initialRows : [['', '']]);

	const onMetadataChange = (updatedRows: string[][]) => {
		setRows(updatedRows);
		const updatedMetadata = mapMetadataRowsToObject(updatedRows);
		onDebouncedChange({ metadata: updatedMetadata });
	};

	const addItem = () => {
		const updatedRows = [...rows, ['', '']] as [string, string][];
		onMetadataChange(updatedRows);
	};

	const handleChange = (value: string, rowIndex: number, itemIndex: number) => {
		const updatedRow = [
			...rows[rowIndex].slice(0, itemIndex),
			value,
			...rows[rowIndex].slice(itemIndex + 1)
		];
		const updatedRows = [...rows.slice(0, rowIndex), updatedRow, ...rows.slice(rowIndex + 1)];
		onMetadataChange(updatedRows);
	};

	return (
		<div className={metadataBlock}>
			<div
				className="header"
				tabIndex={0}
				role="button"
				onClick={() => setCollapsed(!collapsed)}
				onKeyPress={(e) => e.key === 'Enter' && setCollapsed(!collapsed)}>
				<div>Metadata</div>
				<div>
					<BsCaretDown className={collapsed ? 'collapseIconRotate' : 'collapseIcon'} />
				</div>
			</div>

			{!collapsed && (
				<div className="body">
					{rows.map(([key, value], rowIndex) => (
						<Form.Row key={rowIndex} className="row">
							<Col sm={4}>
								<Form.Control
									placeholder="Key"
									defaultValue={key}
									disabled={presetKeys.includes(key)}
									onChange={(e) => handleChange(e.target.value, rowIndex, 0)}
								/>
							</Col>
							<Col>
								{presetFields[key]?.allowed_values && (
									<Select
										defaultValue={{ value, label: value }}
										isClearable={true}
										isSearchable={true}
										options={valuesToOptions(presetFields[key].allowed_values)}
										onChange={(option) => handleChange(option?.value || '', rowIndex, 1)}
									/>
								)}

								{!presetFields[key]?.allowed_values && (
									<Form.Control
										placeholder="Value"
										defaultValue={value}
										onChange={(e) => handleChange(e.target.value, rowIndex, 1)}
									/>
								)}
							</Col>
						</Form.Row>
					))}

					<Button variant="light" onClick={addItem}>
						Add item
					</Button>
				</div>
			)}
		</div>
	);
};

export default Metadata;

const mapMetadataRowsToObject = (rows: string[][]): { [key: string]: string } => {
	const rowsWithDefinedKeys = rows.filter(first);
	return Object.fromEntries(rowsWithDefinedKeys);
};

const valuesToOptions = (values: string[]) => {
	return values.map((value) => ({ value: value, label: value }));
};
